import { LOGIN_SCOPES } from '@/authConfig'
import { type SilentRequest, type AccountInfo, type IPublicClientApplication } from '@azure/msal-browser'

export const acquireAndProcessIdTokenClaims = async (
  msalInstance: IPublicClientApplication,
): Promise<Record<string, any>> => {
  const activeAccount = msalInstance.getActiveAccount() as AccountInfo | null
  if (!activeAccount) {
    return {}
  }

  const silentRequest: SilentRequest = {
    account: activeAccount,
    scopes: LOGIN_SCOPES,
  }
  const result = await msalInstance.acquireTokenSilent(silentRequest)
  return processIdTokenClaims(result.idTokenClaims || null)
}

export const processIdTokenClaims = (idTokenClaims: Record<string, any> | null): Record<string, any> => {
  if (!idTokenClaims) {
    return {}
  }

  // 2025-04-13 Isaac: migrating from pwinno.com to pwinno.onmicrosoft.com
  // from using custom authentication extension to API connector
  // from Tenant using Workplace to using Azure AD B2C
  // for all properties in idTokenClaims that begins with 'extension_',
  for (const key in idTokenClaims) {
    if (key.startsWith('extension_')) {
      // remove the prefix 'extension_' from the key
      const newKey = key.replace('extension_', '')

      const stringData = idTokenClaims[key] as string

      // the data was JSON.stringified
      // parse the data to JSON
      const jsonData = stringData ? JSON.parse(stringData) : null

      // assign the value to the new key
      idTokenClaims[newKey] = jsonData
      // delete the old key
      delete idTokenClaims[key]
    }
  }

  return idTokenClaims
}

export const getPWRoles = (idTokenClaims: any): string[] => {
  // return an empty array if idTokenClaims.pwRoles is null
  if (!idTokenClaims?.pwRoles) {
    return []
  }
  return Array.isArray(idTokenClaims.pwRoles) ? idTokenClaims.pwRoles : [idTokenClaims.pwRoles]
}

export const getClientRoles = (
  idTokenClaims: any,
): {
  [clientCode: string]: string[]
} => {
  // return an empty object if idTokenClaims.pwRoles is null
  if (!idTokenClaims?.pwRoles) {
    return {}
  }

  // if idTokenClaims.pwRoles is string, put it in an array
  const pwRoles = getPWRoles(idTokenClaims)

  // split the role string by colon
  // construct a map of clientCode to roles
  return pwRoles
    ?.filter((role: string) => role.includes(':'))
    .map((role: string) => role.split(':'))
    .reduce((acc: any, [clientCode, role]: string[]) => {
      if (!acc[clientCode]) {
        acc[clientCode] = []
      }
      acc[clientCode].push(role)
      return acc
    }, {})
}

export const getClientCompanyCodes = (
  idTokenClaims: any,
): {
  [clientCode: string]: string[]
} => {
  // return an empty object if idTokenClaims.clientComCodes is null
  if (!idTokenClaims.clientComCodes) {
    return {}
  }

  // if idTokenClaims.clientComCodes is string, put it in an array
  const clientComCodes = Array.isArray(idTokenClaims.clientComCodes)
    ? idTokenClaims.clientComCodes
    : [idTokenClaims.clientComCodes]

  // split the company codes by colon: clientComCodes
  return clientComCodes
    ?.filter((clientComCode: string) => clientComCode.includes(':'))
    ?.map((clientComCode: string) => clientComCode.split(':'))
    .reduce((acc: any, [clientCode, companyCode]: string[]) => {
      if (!acc[clientCode]) {
        acc[clientCode] = []
      }
      acc[clientCode].push(companyCode)
      return acc
    }, {})
}

export const getClientDoctorCodes = (
  idTokenClaims: any,
): {
  [clientCode: string]: string[]
} => {
  // return an empty object if idTokenClaims.clientDocCodes is null
  if (!idTokenClaims.clientDocCodes) {
    return {}
  }

  // if idTokenClaims.clientDocCodes is string, put it in an array
  const clientDocCodes = Array.isArray(idTokenClaims.clientDocCodes)
    ? idTokenClaims.clientDocCodes
    : [idTokenClaims.clientDocCodes]

  // split the doctor codes by colon: doctorCodes
  return clientDocCodes
    ?.filter((doctorCode: string) => doctorCode.includes(':'))
    ?.map((doctorCode: string) => doctorCode.split(':'))
    .reduce((acc: any, [clientCode, doctorCode]: string[]) => {
      if (!acc[clientCode]) {
        acc[clientCode] = []
      }
      acc[clientCode].push(doctorCode)
      return acc
    }, {})
}

export const getClientUserGroups = (
  idTokenClaims: any,
): {
  [clientCode: string]: string[]
} => {
  // return an empty object if idTokenClaims.clientUserGroups is null
  if (!idTokenClaims.clientUserGroups) {
    return {}
  }

  // for clientUserGroups, prepare array like this:
  //  [`${clientUser1.clientCode}:${group1.groupCode},${group2.groupCode}`,`${clientUser2.clientCode}:${group2.groupCode},${group3.groupCode}`]
  // if idTokenClaims.clientUserGroups is string, put it in an array
  let clientUserGroupsByClientCode = idTokenClaims.clientUserGroups

  // log clientUserGroupsByClientCode
  console.log('[getClientUserGroups] clientUserGroupsByClientCode', clientUserGroupsByClientCode)

  // if clientUserGroupsByClientCode is not an array of strings
  if (!Array.isArray(clientUserGroupsByClientCode)) {
    // if clientUserGroupsByClientCode is string, put it in an array
    clientUserGroupsByClientCode = [clientUserGroupsByClientCode]
  }

  // split the user groups by colon for clients, and then by comma for groups
  return clientUserGroupsByClientCode
    ?.filter((clientUserGroup: string) => clientUserGroup.includes(':'))
    ?.map((clientUserGroup: string) => clientUserGroup.split(':'))
    .reduce((acc: any, [clientCode, groupCodes]: string[]) => {
      if (!acc[clientCode]) {
        acc[clientCode] = []
      }
      acc[clientCode].push(...(groupCodes.split(',') ?? []))
      return acc
    }, {})
}

// Helper to check if user is PW Admin (pw:ADMIN)
export const getIsPWAdmin = (idTokenClaims: Record<string, any> | null): boolean => {
  const pwRoles = getPWRoles(idTokenClaims)
  return pwRoles.includes('pw:ADMIN')
}

// Helper to check if user is Lab Admin ({clientId}:ADMIN)
export const getIsLabAdmin = (idTokenClaims: Record<string, any> | null, clientId: string): boolean => {
  const pwRoles = getPWRoles(idTokenClaims)
  return pwRoles.includes(`${clientId}:ADMIN`)
}

// Helper to check if user is Lab User ({clientId}:DOCTOR)
export const getIsLabUser = (idTokenClaims: Record<string, any> | null, clientId: string): boolean => {
  const pwRoles = getPWRoles(idTokenClaims)
  return pwRoles.includes(`${clientId}:DOCTOR`)
}

// Helper to get user role type: 'PWAdmin' | 'LabAdmin' | 'LabUser' | undefined
export const getUserRoleType = (
  idTokenClaims: Record<string, any> | null,
  clientId: string,
): 'PWAdmin' | 'LabAdmin' | 'LabUser' | undefined => {
  if (getIsPWAdmin(idTokenClaims)) return 'PWAdmin'
  if (getIsLabAdmin(idTokenClaims, clientId)) return 'LabAdmin'
  if (getIsLabUser(idTokenClaims, clientId)) return 'LabUser'
  return undefined
}
